import { useEffect, useState } from "react";
import api, { formatErr } from "../../../lib/api";
import { card, btn, btnGhost, label, inputStyle } from "../roads/_roadsCommon";

const STATUS = { PASS: "#22c55e", FAIL: "#ef4444",
                   WARN_LOW_SCOUR: "#f59e0b", WARN_HIGH_SCOUR: "#f59e0b" };

/** Ch 22.2 — Culvert placer with Manning's hydraulic capacity check. */
export default function CulvertPanel({ projectId }) {
  const [palette, setPalette] = useState({ culverts: [] });
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({
    name: "CV-1", culv_type: "box", design_flow_m3_s: 4,
    width_m: 2, height_m: 1.5, diameter_m: null,
    slope: 0.005, manning_n: 0.014, length_m: 12, skew_deg: 0,
  });
  const [err, setErr] = useState("");

  const reload = async () => {
    try {
      const [p, c] = await Promise.all([
        api.get(`/projects/${projectId}/structures/palette`),
        api.get(`/projects/${projectId}/structures/culverts`),
      ]);
      setPalette(p.data); setItems(c.data.culverts || []);
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, []);

  const create = async () => {
    const body = { ...form };
    if (form.culv_type === "pipe") { body.width_m = null; body.height_m = null;
                                          body.diameter_m = form.diameter_m || 0.6; }
    else { body.diameter_m = null; }
    try {
      await api.post(`/projects/${projectId}/structures/culverts`, body);
      reload();
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete culvert?")) return;
    await api.delete(`/projects/${projectId}/structures/culverts/${id}`); reload();
  };

  return (
    <div data-testid="culvert-panel" style={{ display: "grid",
            gridTemplateColumns: "320px 1fr 280px", gap: 8, height: "calc(100vh - 280px)", minHeight: 700 }}>
      <div style={{ ...card, padding: 8 }}>
        <div style={label}>New culvert</div>
        <div style={{ marginTop: 6, fontSize: 10 }}>Name</div>
        <input data-testid="cv-name" style={inputStyle} value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <div style={{ marginTop: 4, fontSize: 10 }}>Type</div>
        <select data-testid="cv-type" style={inputStyle} value={form.culv_type}
          onChange={(e) => setForm({ ...form, culv_type: e.target.value })}>
          {(palette.culverts || []).map((c) =>
            <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
        <div style={{ marginTop: 4, fontSize: 10 }}>Design flow Q (m³/s)</div>
        <input data-testid="cv-q" style={inputStyle} type="number" step="0.1"
          value={form.design_flow_m3_s} onChange={(e) =>
            setForm({ ...form, design_flow_m3_s: parseFloat(e.target.value) || 0 })} />
        {form.culv_type === "pipe" ? (
          <>
            <div style={{ marginTop: 4, fontSize: 10 }}>Diameter (m)</div>
            <input data-testid="cv-d" style={inputStyle} type="number" step="0.1"
              value={form.diameter_m || ""} onChange={(e) =>
                setForm({ ...form, diameter_m: parseFloat(e.target.value) || 0 })} />
          </>
        ) : (
          <>
            <div style={{ marginTop: 4, fontSize: 10 }}>Width × Height (m)</div>
            <div style={{ display: "flex", gap: 4 }}>
              <input data-testid="cv-w" style={inputStyle} type="number" step="0.1"
                value={form.width_m} onChange={(e) =>
                  setForm({ ...form, width_m: parseFloat(e.target.value) || 0 })} />
              <input data-testid="cv-h" style={inputStyle} type="number" step="0.1"
                value={form.height_m} onChange={(e) =>
                  setForm({ ...form, height_m: parseFloat(e.target.value) || 0 })} />
            </div>
          </>
        )}
        <div style={{ marginTop: 4, fontSize: 10 }}>Slope · Manning n · Length (m)</div>
        <div style={{ display: "flex", gap: 4 }}>
          <input data-testid="cv-slope" style={inputStyle} type="number" step="0.001"
            value={form.slope} onChange={(e) =>
              setForm({ ...form, slope: parseFloat(e.target.value) || 0 })} />
          <input data-testid="cv-n" style={inputStyle} type="number" step="0.001"
            value={form.manning_n} onChange={(e) =>
              setForm({ ...form, manning_n: parseFloat(e.target.value) || 0.014 })} />
          <input data-testid="cv-l" style={inputStyle} type="number"
            value={form.length_m} onChange={(e) =>
              setForm({ ...form, length_m: parseFloat(e.target.value) || 0 })} />
        </div>
        <button data-testid="cv-create" style={{ ...btn, width: "100%", marginTop: 12 }}
          onClick={create}>+ Create culvert</button>
      </div>

      <div style={{ ...card, padding: 8, overflowY: "auto" }}>
        <div style={label}>Culverts ({items.length})</div>
        {items.map((c) => {
          const k = c.hydraulic_check;
          return (
            <div key={c.id} data-testid={`cv-row-${c.id}`} style={{ padding: 8, marginTop: 6,
                  background: "rgba(255,255,255,0.04)", borderRadius: 4,
                  borderLeft: `3px solid ${STATUS[k.capacity_status] || "#888"}` }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <b>{c.name}</b>
                <button style={{ ...btnGhost, padding: "1px 6px", fontSize: 10 }}
                  onClick={() => remove(c.id)}>×</button>
              </div>
              <div style={{ fontSize: 11, color: "#aaa", marginTop: 4 }}>
                Type: {c.culv_type} · {c.culv_type === "pipe"
                  ? `Ø ${c.diameter_m}m`
                  : `${c.width_m}m × ${c.height_m}m`} · slope {c.slope} · n {c.manning_n} · L {c.length_m}m
              </div>
              <div style={{ fontSize: 11, fontFamily: "monospace", marginTop: 4 }}>
                Q_design = {c.design_flow_m3_s} m³/s ·
                Q_capacity = <b style={{ color: "#3aa0ff" }}>{k.capacity_m3_s}</b> m³/s ·
                ratio <b style={{ color: STATUS[k.capacity_status] }}>{k.capacity_ratio}</b>
              </div>
              <div style={{ fontSize: 11, marginTop: 2 }}>
                V = {k.velocity_m_s} m/s ·
                <span style={{ color: STATUS[k.capacity_status], fontWeight: 600, marginLeft: 4 }}>
                  Capacity {k.capacity_status}
                </span>
                · <span style={{ color: STATUS[k.velocity_status], fontWeight: 600 }}>
                  V {k.velocity_status}</span>
              </div>
            </div>
          );
        })}
        {!items.length && <div style={{ color: "#888", fontSize: 11, padding: 12 }}>
          No culverts yet — create one.</div>}
      </div>

      <div style={{ ...card, padding: 8 }}>
        <div style={label}>Manning hydraulics</div>
        <p style={{ fontSize: 10, color: "#aaa", lineHeight: 1.6 }}>
          Q = (1/n)·A·R<sup>2/3</sup>·S<sup>1/2</sup>. Velocity bands: 0.6–4.0 m/s OK; below
          0.6 risks siltation (LOW_SCOUR), above 4.0 risks abrasion (HIGH_SCOUR). Capacity check
          requires Q<sub>cap</sub> ≥ Q<sub>design</sub>. References IRC SP-13.
        </p>
        {err && <div style={{ marginTop: 8, color: "#FF8888", fontSize: 11 }}>{err}</div>}
      </div>
    </div>
  );
}
